import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { isInfluencerVisibleInBudgetScenario } from './influencerBudgetVisibility'
import { OVERVIEW_SCOPE_KEYS, REELS_LIKE_TABLE_KEYS, AUTH_PROVIDER_TO_CHANNEL_COLUMN, OTHER_BUDGET_CONFIG } from './BudgetDetailsPanelConfig'
import {
  buildEmptyReelsLikeRowsByKey,
  buyoutRowFromInfluencer,
  buyoutRowsFromCampaign,
  customFieldBudgetRowsFromCampaign,
  orderedInfluencerSlotsForScenario,
  overallKostenRowFromInfluencer,
  overviewRowFromBase,
  reelsLikeRowsForTableKey,
  scenarioActionGroupsFingerprint,
  scenarioActionsFingerprint,
  scenarioCustomFieldValuesFingerprint,
  scenarioInfluencerRowBases,
} from './BudgetDetailsPanelRowBuilders'

function rowSlotKey(influencerId, slotNumber) {
  return `${influencerId}:${Number(slotNumber) || 1}`
}

function applyPendingWelcheBuyoutsEditsToRows(rows, pendingEdits) {
  if (!pendingEdits || typeof pendingEdits !== 'object') return rows
  return rows.map(row => {
    if (row?.influencerId == null) return row
    const key = rowSlotKey(row.influencerId, row.slotNumber)
    if (!(key in pendingEdits)) return row
    return { ...row, welcheBuyouts: pendingEdits[key] }
  })
}

function pendingOverviewScopeEditKey(influencerId, slotNumber, colKey) {
  return `${rowSlotKey(influencerId, slotNumber)}:${colKey}`
}

const OVERVIEW_CHANNEL_COLUMN_KEYS = [
  ...Object.values(AUTH_PROVIDER_TO_CHANNEL_COLUMN),
  OTHER_BUDGET_CONFIG.channelKey,
]

function applyPendingOverviewScopeEditsToRows(rows, pendingEdits) {
  return rows.map(row => {
    if (!row?.influencerId) return row
    let next = row
    for (const colKey of OVERVIEW_SCOPE_KEYS) {
      const key = pendingOverviewScopeEditKey(row.influencerId, row.slotNumber, colKey)
      if (!(key in pendingEdits)) continue
      next = next === row ? { ...row } : next
      next[colKey] = pendingEdits[key]
    }
    return next
  })
}

/** Keep in-flight channel names and unlocked scope cells when scenario data triggers a row rebuild. */
function mergeOverviewRowsWithPrevious(freshRows, previousRows) {
  if (!previousRows?.length) return freshRows
  const prevByKey = new Map(previousRows.map(row => [rowSlotKey(row.influencerId, row.slotNumber), row]))
  return freshRows.map(fresh => {
    const prev = prevByKey.get(rowSlotKey(fresh.influencerId, fresh.slotNumber))
    if (!prev) return fresh

    const merged = { ...fresh }
    for (const channelKey of OVERVIEW_CHANNEL_COLUMN_KEYS) {
      const prevVal = String(prev[channelKey] ?? '').trim()
      const freshVal = String(fresh[channelKey] ?? '').trim()
      if (prevVal && !freshVal) merged[channelKey] = prev[channelKey]
    }

    const disabledCells = { ...(fresh._disabledCells || {}) }
    for (const scopeKey of OVERVIEW_SCOPE_KEYS) {
      if (prev._disabledCells && !prev._disabledCells[scopeKey]) {
        delete disabledCells[scopeKey]
      }
    }
    merged._disabledCells = disabledCells
    merged._authenticationsByProvider = {
      ...(fresh._authenticationsByProvider || {}),
      ...(prev._authenticationsByProvider || {}),
    }
    merged._lockedCells = { ...(fresh._lockedCells || {}), ...(prev._lockedCells || {}) }
    return merged
  })
}

export default function useBudgetRows({
  campaignScenario,
  influencers,
  campaign,
  authentications,
  customFieldsKey,
}) {
  const [overviewRows, setOverviewRows] = useState([])
  const [pendingOverviewScopeEdits, setPendingOverviewScopeEdits] = useState({})
  const pendingOverviewScopeEditsRef = useRef({})
  const [pendingWelcheBuyoutsEdits, setPendingWelcheBuyoutsEdits] = useState({})
  const pendingWelcheBuyoutsEditsRef = useRef({})
  const [reelsLikeRowsByKey, setReelsLikeRowsByKey] = useState(buildEmptyReelsLikeRowsByKey)
  const [overallKostenRows, setOverallKostenRows] = useState([])
  const [buyoutRows, setBuyoutRows] = useState([])
  const [customFieldBudgetRows, setCustomFieldBudgetRows] = useState([])

  const customFieldBudgetRowsRef = useRef([])
  const overviewRowsRef = useRef([])
  const reelsLikeRowsByKeyRef = useRef({})
  const overallKostenRowsRef = useRef([])
  const buyoutRowsRef = useRef([])
  const campaignScenarioRef = useRef(campaignScenario)

  useEffect(() => {
    customFieldBudgetRowsRef.current = customFieldBudgetRows
  }, [customFieldBudgetRows])

  useEffect(() => {
    overviewRowsRef.current = overviewRows
  }, [overviewRows])

  useEffect(() => {
    reelsLikeRowsByKeyRef.current = reelsLikeRowsByKey
  }, [reelsLikeRowsByKey])

  useEffect(() => {
    overallKostenRowsRef.current = overallKostenRows
  }, [overallKostenRows])

  useEffect(() => {
    buyoutRowsRef.current = buyoutRows
  }, [buyoutRows])

  useEffect(() => {
    campaignScenarioRef.current = campaignScenario
  }, [campaignScenario])

  useEffect(() => {
    pendingOverviewScopeEditsRef.current = pendingOverviewScopeEdits
  }, [pendingOverviewScopeEdits])

  useEffect(() => {
    pendingWelcheBuyoutsEditsRef.current = pendingWelcheBuyoutsEdits
  }, [pendingWelcheBuyoutsEdits])

  const setPendingOverviewScopeEdit = useCallback((influencerId, slotNumber, colKey, value) => {
    setPendingOverviewScopeEdits(prev => ({
      ...prev,
      [pendingOverviewScopeEditKey(influencerId, slotNumber, colKey)]: value,
    }))
  }, [])

  const clearPendingOverviewScopeEdit = useCallback((influencerId, slotNumber, colKey, expectedValue) => {
    setPendingOverviewScopeEdits(prev => {
      const key = pendingOverviewScopeEditKey(influencerId, slotNumber, colKey)
      if (!(key in prev)) return prev
      if (expectedValue !== undefined && String(prev[key]) !== String(expectedValue)) return prev
      const next = { ...prev }
      delete next[key]
      return next
    })
  }, [])

  const setPendingWelcheBuyoutsEdit = useCallback((influencerId, slotNumber, value) => {
    setPendingWelcheBuyoutsEdits(prev => ({
      ...prev,
      [rowSlotKey(influencerId, slotNumber)]: value,
    }))
  }, [])

  const clearPendingWelcheBuyoutsEdit = useCallback((influencerId, slotNumber, expectedValue) => {
    setPendingWelcheBuyoutsEdits(prev => {
      const key = rowSlotKey(influencerId, slotNumber)
      if (!(key in prev)) return prev
      if (expectedValue !== undefined && String(prev[key]) !== String(expectedValue)) return prev
      const next = { ...prev }
      delete next[key]
      return next
    })
  }, [])

  const applyPendingOverviewScopeEdits = useCallback(
    rows => applyPendingOverviewScopeEditsToRows(rows, pendingOverviewScopeEdits),
    [pendingOverviewScopeEdits]
  )

  const pendingWelcheBuyoutsEditHandlers = useMemo(
    () => ({
      setPendingWelcheBuyoutsEdit,
      clearPendingWelcheBuyoutsEdit,
    }),
    [clearPendingWelcheBuyoutsEdit, setPendingWelcheBuyoutsEdit]
  )

  const pendingOverviewScopeEditHandlers = useMemo(
    () => ({
      setPendingOverviewScopeEdit,
      clearPendingOverviewScopeEdit,
      applyPendingOverviewScopeEdits,
    }),
    [applyPendingOverviewScopeEdits, clearPendingOverviewScopeEdit, setPendingOverviewScopeEdit]
  )

  const scenarioActionsSyncKey = scenarioActionsFingerprint(campaignScenario?.actions)
  const scenarioActionGroupsSyncKey = scenarioActionGroupsFingerprint(campaignScenario?.campaign_scenario_action_groups)
  const authenticationsStatusSyncKey = useMemo(
    () => (authentications || []).map(a => `${a.authentication_id}:${a.status_id ?? ''}`).join(','),
    [authentications]
  )
  const scenarioCustomFieldValuesSyncKey = scenarioCustomFieldValuesFingerprint(
    campaignScenario?.campaign_custom_field_values
  )

  useEffect(() => {
    if (!campaignScenario) return
    if (!Array.isArray(influencers)) return
    setOverviewRows(prev => {
      const bases = scenarioInfluencerRowBases(campaignScenario, influencers).filter(base => {
        const inf = influencers.find(i => Number(i.id) === Number(base.influencerId))
        return inf && isInfluencerVisibleInBudgetScenario(campaignScenario, inf, campaign, authentications, base.slotNumber)
      })
      const freshRows = applyPendingOverviewScopeEditsToRows(
        bases.map(base => overviewRowFromBase(base, campaign?.currency_symbol)),
        pendingOverviewScopeEditsRef.current
      )
      return mergeOverviewRowsWithPrevious(freshRows, prev)
    })
  }, [
    campaignScenario,
    influencers,
    scenarioActionsSyncKey,
    scenarioActionGroupsSyncKey,
    authenticationsStatusSyncKey,
    campaign,
    authentications,
  ])

  useEffect(() => {
    if (!campaignScenario) return
    if (!Array.isArray(influencers)) return
    const currencySym = campaign?.currency_symbol || '€'
    setReelsLikeRowsByKey(
      REELS_LIKE_TABLE_KEYS.reduce((acc, k) => {
        acc[k] = reelsLikeRowsForTableKey(campaignScenario, k, influencers, currencySym, campaign, authentications)
        return acc
      }, {})
    )
    setOverallKostenRows(
      orderedInfluencerSlotsForScenario(campaignScenario, influencers)
        .filter(({ influencer, slotNumber }) =>
          isInfluencerVisibleInBudgetScenario(campaignScenario, influencer, campaign, authentications, slotNumber)
        )
        .map(({ influencer, slotNumber }) => overallKostenRowFromInfluencer(influencer, campaignScenario, currencySym, campaign, slotNumber))
    )
    setBuyoutRows(
      applyPendingWelcheBuyoutsEditsToRows(
        buyoutRowsFromCampaign(campaignScenario, influencers, campaign, authentications),
        pendingWelcheBuyoutsEditsRef.current
      )
    )
    setCustomFieldBudgetRows(customFieldBudgetRowsFromCampaign(campaignScenario, influencers, campaign, authentications))
  }, [
    campaignScenario,
    influencers,
    scenarioActionsSyncKey,
    scenarioActionGroupsSyncKey,
    authenticationsStatusSyncKey,
    scenarioCustomFieldValuesSyncKey,
    campaign?.currency_symbol,
    campaign,
    authentications,
    customFieldsKey,
  ])

  return {
    overviewRows,
    setOverviewRows,
    reelsLikeRowsByKey,
    setReelsLikeRowsByKey,
    overallKostenRows,
    setOverallKostenRows,
    buyoutRows,
    setBuyoutRows,
    customFieldBudgetRows,
    setCustomFieldBudgetRows,
    refs: {
      customFieldBudgetRowsRef,
      overviewRowsRef,
      reelsLikeRowsByKeyRef,
      overallKostenRowsRef,
      buyoutRowsRef,
      pendingWelcheBuyoutsEditsRef,
      campaignScenarioRef,
    },
    pendingOverviewScopeEdits: pendingOverviewScopeEditHandlers,
    pendingWelcheBuyoutsEdits: pendingWelcheBuyoutsEditHandlers,
    syncKeys: {
      scenarioActionsSyncKey,
      scenarioActionGroupsSyncKey,
      authenticationsStatusSyncKey,
      scenarioCustomFieldValuesSyncKey,
    },
  }
}
